import { createSlice } from "@reduxjs/toolkit"
import axios from "axios"

// Customer support tickets.
//
// A ticket is a subject + a thread of messages. Statuses are open / resolved /
// closed; a resolved or closed ticket is LOCKED and neither side can post to it
// any more. The server decides that and hands back `can_reply` on the detail
// response — screens trust that flag instead of re-deriving it from the status,
// so a rule change on the backend does not need a matching change here.
//
// The thread is the interesting part of this slice: it is polled while open (see
// hooks/usePollWhileVisible) and re-fetched after every post rather than patched
// locally, because the server owns the message id, its timestamp and the
// attachment URLs — and a staff reply may have landed in between.
//
// NOTE: this slice is in the persist blacklist (see src/store.js). Ticket bodies
// are free text and can carry order/payment details, so they are kept in memory
// only rather than written to localStorage on a possibly shared device.
const initialState = {
    listLoading: false,
    listLoaded: false,
    list: [],
    pageCount: 1,
    currentPage: 1,
    totalRow: 0,
    listErrorMessage: null,

    threadLoading: false,
    thread: null,
    threadErrorMessage: null,

    sending: false,
    sendFail: false,
    sendErrorMessage: null,

    creating: false,
    createFail: false,
    createErrorMessage: null,
}

export const supportSlice = createSlice({
    name: "support",
    initialState,
    reducers: {
        listRequest: (state) => {
            state.listLoading = true
            state.listErrorMessage = null
        },
        listSuccess: (state, action) => {
            state.listLoading = false
            state.listLoaded = true
            state.list = action.payload?.data || []
            // The list envelope puts paging at the TOP level, next to `message`.
            state.pageCount = action.payload?.page_count || 1
            state.currentPage = action.payload?.current_page || 1
            state.totalRow = action.payload?.total_row || 0
        },
        listFail: (state, action) => {
            state.listLoading = false
            state.listErrorMessage = action.payload
        },

        threadRequest: (state) => {
            state.threadLoading = true
            state.threadErrorMessage = null
        },
        threadSuccess: (state, action) => {
            state.threadLoading = false
            state.thread = action.payload || null
        },
        threadFail: (state, action) => {
            state.threadLoading = false
            state.threadErrorMessage = action.payload
        },
        clearThread: (state) => {
            state.thread = null
            state.threadLoading = false
            state.threadErrorMessage = null
            state.sending = false
            state.sendFail = false
            state.sendErrorMessage = null
        },

        sendRequest: (state) => {
            state.sending = true
            state.sendFail = false
            state.sendErrorMessage = null
        },
        sendSuccess: (state) => {
            state.sending = false
        },
        sendFail: (state, action) => {
            state.sending = false
            state.sendFail = true
            state.sendErrorMessage = action.payload
        },

        createRequest: (state) => {
            state.creating = true
            state.createFail = false
            state.createErrorMessage = null
        },
        createSuccess: (state) => {
            state.creating = false
        },
        createFail: (state, action) => {
            state.creating = false
            state.createFail = true
            state.createErrorMessage = action.payload
        },
        resetCreate: (state) => {
            state.creating = false
            state.createFail = false
            state.createErrorMessage = null
        },
    },
})

export const {
    listRequest,
    listSuccess,
    listFail,
    threadRequest,
    threadSuccess,
    threadFail,
    clearThread,
    sendRequest,
    sendSuccess,
    sendFail,
    createRequest,
    createSuccess,
    createFail,
    resetCreate,
} = supportSlice.actions

const message = (error) =>
    error.response?.data?.errorMessage || error.message || "Network error occurred"

// The endpoints accept plain JSON or multipart, and multer is mounted with
// .any() so the field names do not matter. Sending FormData unconditionally
// would push every request through the upload path for no reason, so the
// switch is made here, once, on whether there is actually a file to carry.
const withFiles = (fields, files) => {
    if (!files || files.length === 0) return fields
    const form = new FormData()
    Object.entries(fields).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return
        form.append(key, value)
    })
    files.forEach((file, index) => form.append(`attachment_${index}`, file))
    return form
}

export const doListTickets =
    ({ page = 1, size = 10, status } = {}) =>
    async (dispatch) => {
        dispatch(listRequest())
        try {
            const response = await axios.get("/api/customer/support/tickets", {
                params: { page, size, ...(status ? { status } : {}) },
            })
            dispatch(listSuccess(response.data))
        } catch (error) {
            dispatch(listFail(message(error)))
        }
    }

// `silent` is the polling mode: it neither raises the loading flag nor clears
// the thread on failure, so a blip in connectivity cannot wipe a conversation
// the customer is in the middle of reading. Resolves true/false so the poll
// hook can count consecutive failures and give up.
export const doGetTicket =
    (id, { silent = false } = {}) =>
    async (dispatch) => {
        if (!silent) dispatch(threadRequest())
        try {
            const response = await axios.get(`/api/customer/support/tickets/${id}`)
            dispatch(threadSuccess(response.data.data))
            return true
        } catch (error) {
            if (!silent) dispatch(threadFail(message(error)))
            return false
        }
    }

export const doCreateTicket = (fields, files) => async (dispatch) => {
    dispatch(createRequest())
    try {
        const response = await axios.post(
            "/api/customer/support/tickets",
            withFiles(fields, files),
        )
        dispatch(createSuccess())
        return response.data.data
    } catch (error) {
        dispatch(createFail(message(error)))
        return null
    }
}

export const doSendMessage = (id, body, files) => async (dispatch) => {
    dispatch(sendRequest())
    try {
        await axios.post(
            `/api/customer/support/tickets/${id}/messages`,
            withFiles({ body }, files),
        )
        dispatch(sendSuccess())
        await dispatch(doGetTicket(id, { silent: true }))
        return true
    } catch (error) {
        dispatch(sendFail(message(error)))
        // 409 means staff resolved or closed the ticket between this screen
        // loading and the reply being sent. Re-fetching swaps the composer for
        // the locked notice, so the customer is not left retyping into a box
        // that can never accept the message.
        if (error.response?.status === 409) {
            await dispatch(doGetTicket(id, { silent: true }))
        }
        return false
    }
}

export const getTickets = (state) => state.support.list
export const getTicketsLoading = (state) => state.support.listLoading
export const getTicketsLoaded = (state) => state.support.listLoaded
export const getTicketsPageCount = (state) => state.support.pageCount
export const getTicketsTotalRow = (state) => state.support.totalRow
export const getTicketsErrorMessage = (state) => state.support.listErrorMessage

export const getTicket = (state) => state.support.thread
export const getTicketLoading = (state) => state.support.threadLoading
export const getTicketErrorMessage = (state) => state.support.threadErrorMessage

export const getTicketSending = (state) => state.support.sending
export const getTicketSendFail = (state) => state.support.sendFail
export const getTicketSendErrorMessage = (state) => state.support.sendErrorMessage

export const getTicketCreating = (state) => state.support.creating
export const getTicketCreateFail = (state) => state.support.createFail
export const getTicketCreateErrorMessage = (state) => state.support.createErrorMessage

export default supportSlice.reducer
